import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useObserver } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import { useDevicePairingUIStore } from '@core/useStores';
import { PairableUser } from 'AuthPart/DevicePairing/services';

import { messages } from 'AuthPart/DevicePairing/devicePairing.messages';
import { useStyles } from './PairDeviceModal.styles';
import { ThemeProps } from '@styles/theme';

const SEARCH_DEBOUNCE_MS = 350;

/**
 * Chooses who a device is being paired for, shown only to the roles allowed to pair for others.
 *
 * The default is always "Me". Pairing somebody else's headset is the rarer, heavier act — it hands
 * a standing session to a person who is not at the keyboard — so it is the branch you have to
 * choose, never the one you land on.
 */
export const PairForPicker: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const devicePairingUIStore = useDevicePairingUIStore();

    const [query, setQuery] = useState('');
    const debounce = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(debounce.current), []);

    const search = (value: string) => {
        setQuery(value);

        clearTimeout(debounce.current);
        debounce.current = setTimeout(() => devicePairingUIStore.searchPairableUsers(value), SEARCH_DEBOUNCE_MS);
    };

    const chooseSelf = () => {
        setQuery('');
        devicePairingUIStore.choosePairForSelf();
    };

    const chooseSomeoneElse = () => {
        devicePairingUIStore.choosePairForOther();
        devicePairingUIStore.searchPairableUsers(query);
    };

    const pick = (user: PairableUser) => {
        devicePairingUIStore.setPairFor(user);
    };

    return useObserver(() => {
        const { pairFor, pairForOther, pairableUsers, pairableUsersStatus } = devicePairingUIStore;

        const renderResults = () => {
            if (pairableUsersStatus.isLoading) {
                return <p className={styles.pickerHint}>{messages['devicePairing.pairFor.searching']}</p>;
            }

            if (!pairableUsers.length) {
                return <p className={styles.pickerHint}>{messages['devicePairing.pairFor.noResults']}</p>;
            }

            return (
                <ul className={styles.pickerList}>
                    {
                        pairableUsers.map(user => (
                            <li key={user.id}>
                                <button
                                    type="button"
                                    className={classNames(styles.pickerOption, {
                                        [styles.pickerOptionSelected]: pairFor?.id === user.id,
                                    })}
                                    onClick={() => pick(user)}
                                >
                                    <span className={styles.pickerOptionName}>
                                        {user.fullName?.trim() || user.email}
                                    </span>
                                    <span className={styles.pickerOptionEmail}>{user.email}</span>
                                </button>
                            </li>
                        ))
                    }
                </ul>
            );
        };

        return (
            <div className={styles.picker} data-testid="pair-for-picker">
                <span className={styles.pickerLegend}>{messages['devicePairing.pairFor.legend']}</span>

                <div className={styles.pickerToggle} role="radiogroup">
                    <button
                        type="button"
                        role="radio"
                        aria-checked={!pairForOther}
                        className={classNames(styles.pickerToggleButton, {
                            [styles.pickerToggleButtonActive]: !pairForOther,
                        })}
                        onClick={chooseSelf}
                    >
                        {messages['devicePairing.pairFor.self']}
                    </button>

                    <button
                        type="button"
                        role="radio"
                        aria-checked={pairForOther}
                        className={classNames(styles.pickerToggleButton, {
                            [styles.pickerToggleButtonActive]: pairForOther,
                        })}
                        onClick={chooseSomeoneElse}
                    >
                        {messages['devicePairing.pairFor.someoneElse']}
                    </button>
                </div>

                {
                    pairForOther && (
                        <>
                            <div className={styles.pickerSearch}>
                                <SearchOutlinedIcon className={styles.pickerSearchIcon} />
                                <input
                                    className={styles.pickerSearchInput}
                                    value={query}
                                    autoFocus
                                    placeholder={messages['devicePairing.pairFor.searchPlaceholder']}
                                    onChange={event => search(event.target.value)}
                                />
                            </div>

                            {renderResults()}
                        </>
                    )
                }
            </div>
        );
    });
}
